import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SellerLayout } from '../components/SellerLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { colors } from '../constants/colors';
import { getSalesBySeller, cancelSale } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import type { ItemSaleWithPayment, PaymentStatus } from '../types';
import { Package, AlertTriangle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { parseUTC } from '../utils/date';
import { Img } from '../components/Img';

const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: PaymentStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'AUTHORIZED', label: 'Authorized' },
  { value: 'CAPTURED', label: 'Paid' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REFUNDED', label: 'Refunded' },
];

export function SellerSalesPage() {
  useDocumentTitle('Seller Sales');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [orders, setOrders] = useState<ItemSaleWithPayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get filter and page from URL
  const statusFilter = (searchParams.get('status') as PaymentStatus | 'ALL') || 'ALL';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    if (user?.id) {
      loadOrders();
    }
  }, [user?.id]);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const data = await getSalesBySeller();
      // Sort by createdAt descending (newest first)
      data.sort((a, b) => parseUTC(b.createdAt).getTime() - parseUTC(a.createdAt).getTime());
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter orders based on status
  const filteredOrders = useMemo(() => {
    if (statusFilter === 'ALL') return orders;
    return orders.filter(o => o.paymentStatus === statusFilter);
  }, [orders, statusFilter]);

  // Paginate filtered orders
  const totalPages = Math.ceil(filteredOrders.length / PAGE_SIZE);
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, currentPage]);

  // Failed orders for alert banner (separate from filtered view)
  const failedOrdersCount = orders.filter(o => o.paymentStatus === 'FAILED').length;

  const setStatusFilter = (status: PaymentStatus | 'ALL') => {
    const params = new URLSearchParams(searchParams);
    if (status === 'ALL') {
      params.delete('status');
    } else {
      params.set('status', status);
    }
    params.set('page', '1'); // Reset to page 1 when filter changes
    setSearchParams(params);
  };

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const getPaymentStatusColor = (status: PaymentStatus | undefined): React.CSSProperties => {
    switch (status) {
      case 'PENDING':
        return { backgroundColor: '#F59E0B', color: colors.white };
      case 'AUTHORIZED':
        return { backgroundColor: '#3B82F6', color: colors.white };
      case 'CAPTURED':
        return { backgroundColor: '#22C55E', color: colors.white };
      case 'FAILED':
        return { backgroundColor: '#DC2626', color: colors.white };
      case 'CANCELLED':
        return { backgroundColor: '#6B7280', color: colors.white };
      case 'REFUNDED':
        return { backgroundColor: '#8B5CF6', color: colors.white };
      case 'PARTIALLY_REFUNDED':
        return { backgroundColor: '#A855F7', color: colors.white };
      default:
        return { backgroundColor: '#6B7280', color: colors.white };
    }
  };

  const getPaymentStatusLabel = (status: PaymentStatus | undefined): string => {
    switch (status) {
      case 'PENDING': return 'Pending';
      case 'AUTHORIZED': return 'Authorized';
      case 'CAPTURED': return 'Paid';
      case 'FAILED': return 'Failed';
      case 'CANCELLED': return 'Cancelled';
      case 'REFUNDED': return 'Refunded';
      case 'PARTIALLY_REFUNDED': return 'Partial Refund';
      default: return 'Unknown';
    }
  };

  const [hoveredOrderId, setHoveredOrderId] = useState<string | null>(null);
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);

  const handleCancelOrder = async (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (cancellingOrderId) return;

    if (!confirm('Are you sure you want to cancel this sale?')) return;

    setCancellingOrderId(orderId);
    try {
      await cancelSale(orderId);
      await loadOrders();
    } catch (err) {
      console.error('Failed to cancel order:', err);
      alert('Failed to cancel order. Please try again.');
    } finally {
      setCancellingOrderId(null);
    }
  };

  const renderOrderRow = (order: ItemSaleWithPayment) => (
    <div
      key={order.id}
      style={{
        ...styles.tableRow,
        ...(hoveredOrderId === order.id ? styles.tableRowHover : {}),
      }}
      onClick={() => navigate(`/sell/sales/${order.id}`)}
      onMouseEnter={() => setHoveredOrderId(order.id)}
      onMouseLeave={() => setHoveredOrderId(null)}
    >
      <div style={styles.tableCell}>
        <div style={styles.orderItemInfo}>
          {order.itemSnapshot?.imageUrl ? (
            <Img src={order.itemSnapshot.imageUrl} alt={order.itemSnapshot.name ?? ''} variant="thumb" renderedWidth={40} blurhash={order.itemSnapshot.blurhash} style={styles.orderItemImage} />
          ) : (
            <div style={styles.orderItemImagePlaceholder}>
              <Package size={16} color={colors.gray[400]} />
            </div>
          )}
          <span style={styles.orderItemName}>{order.itemSnapshot?.name ?? 'Unknown Item'}</span>
        </div>
      </div>
      <span style={styles.tableCell}>
        {order.buyerUsername ? (
          <a
            href={`/user/${order.buyerUsername}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              navigate(`/user/${order.buyerUsername}`);
            }}
            style={styles.buyerLink}
          >
            @{order.buyerUsername}
          </a>
        ) : (
          <span>{order.buyerId?.slice(0, 8) || 'N/A'}</span>
        )}
      </span>
      <span style={styles.tableCell}>${(order.total ?? 0).toFixed(2)}</span>
      <span style={styles.tableCell}>
        <span style={{ ...styles.statusBadge, ...getPaymentStatusColor(order.paymentStatus) }}>
          {getPaymentStatusLabel(order.paymentStatus)}
        </span>
      </span>
      <span style={styles.tableCell}>
        {parseUTC(order.createdAt).toLocaleDateString()}
      </span>
      <span style={styles.tableCell} onClick={(e) => e.stopPropagation()}>
        {(order.paymentStatus === 'FAILED' || order.paymentStatus === 'PENDING') && (
          <button
            style={styles.cancelButton}
            onClick={(e) => handleCancelOrder(order.id, e)}
            disabled={cancellingOrderId === order.id}
            title="Cancel order"
          >
            {cancellingOrderId === order.id ? (
              <span style={styles.cancelButtonLoading}>...</span>
            ) : (
              <X size={16} />
            )}
          </button>
        )}
      </span>
    </div>
  );

  return (
    <SellerLayout>
      {isLoading ? (
        <div style={styles.loadingState}>Loading sales...</div>
      ) : orders.length === 0 ? (
        <section style={styles.section}>
          <div style={styles.emptyState}>
            <Package size={48} color={colors.gray[300]} />
            <p style={{ marginTop: '12px' }}>No sales yet</p>
            <p style={{ fontSize: '14px', color: colors.gray[600] }}>
              Sales will appear here when customers win auctions
            </p>
          </div>
        </section>
      ) : (
        <>
          {/* Failed Sales Alert Banner */}
          {failedOrdersCount > 0 && statusFilter !== 'FAILED' && (
            <section style={styles.alertBanner}>
              <div style={styles.alertContent}>
                <AlertTriangle size={18} color="#DC2626" />
                <span style={styles.alertText}>
                  {failedOrdersCount} sale{failedOrdersCount > 1 ? 's have' : ' has'} failed payment
                </span>
              </div>
              <button
                style={styles.alertButton}
                onClick={() => setStatusFilter('FAILED')}
              >
                View Failed
              </button>
            </section>
          )}

          {/* Sales Section */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Sales</h2>
              <span style={styles.orderCount}>
                {filteredOrders.length} {filteredOrders.length === 1 ? 'sale' : 'sales'}
              </span>
            </div>

            {/* Status Filters */}
            <div style={styles.filterRow}>
              {STATUS_OPTIONS.map((option) => {
                const isActive = statusFilter === option.value;
                const count = option.value === 'ALL'
                  ? orders.length
                  : orders.filter(o => o.paymentStatus === option.value).length;

                if (option.value !== 'ALL' && count === 0) return null;

                return (
                  <button
                    key={option.value}
                    onClick={() => setStatusFilter(option.value)}
                    style={{
                      ...styles.filterButton,
                      ...(isActive ? styles.filterButtonActive : {}),
                    }}
                  >
                    {option.label}
                    <span style={{
                      ...styles.filterCount,
                      ...(isActive ? styles.filterCountActive : {}),
                    }}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Sales Table */}
            {paginatedOrders.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={{ color: colors.gray[600] }}>No sales match this filter</p>
              </div>
            ) : (
              <>
                <div style={{ overflowX: isMobile ? 'auto' : undefined, WebkitOverflowScrolling: 'touch' }}>
                <div style={{ ...styles.ordersTable, minWidth: isMobile ? '800px' : undefined }}>
                  <div style={styles.tableHeader}>
                    <span style={styles.tableHeaderCell}>Item</span>
                    <span style={styles.tableHeaderCell}>Buyer</span>
                    <span style={styles.tableHeaderCell}>Amount</span>
                    <span style={styles.tableHeaderCell}>Payment</span>
                    <span style={styles.tableHeaderCell}>Date</span>
                    <span style={styles.tableHeaderCell}></span>
                  </div>
                  {paginatedOrders.map((order) => renderOrderRow(order))}
                </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={styles.pagination}>
                    <button
                      style={{
                        ...styles.pageButton,
                        ...(currentPage === 1 ? styles.pageButtonDisabled : {}),
                      }}
                      onClick={() => setPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={18} />
                      Previous
                    </button>
                    <span style={styles.pageInfo}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      style={{
                        ...styles.pageButton,
                        ...(currentPage === totalPages ? styles.pageButtonDisabled : {}),
                      }}
                      onClick={() => setPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '20px',
    border: `1px solid ${colors.gray[300]}`,
  },
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FEF2F2',
    borderRadius: '12px',
    padding: '12px 16px',
    border: '1px solid #FECACA',
    marginBottom: '16px',
  },
  alertContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  alertText: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#991B1B',
  },
  alertButton: {
    padding: '6px 12px',
    backgroundColor: '#DC2626',
    color: colors.white,
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    margin: 0,
    color: colors.ink.black,
  },
  orderCount: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: colors.gray[100],
    color: colors.gray[700],
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  filterButtonActive: {
    backgroundColor: colors.ink.black,
    color: colors.white,
  },
  filterCount: {
    padding: '2px 6px',
    backgroundColor: colors.gray[200],
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: 600,
  },
  filterCountActive: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: colors.white,
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px',
    color: colors.gray[600],
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: colors.gray[600],
  },
  ordersTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 60px',
    gap: '16px',
    padding: '12px 16px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px 8px 0 0',
    fontWeight: 600,
    fontSize: '13px',
    color: colors.gray[600],
  },
  tableHeaderCell: {
    display: 'flex',
    alignItems: 'center',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 60px',
    gap: '16px',
    padding: '16px',
    borderBottom: `1px solid ${colors.gray[300]}`,
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  tableRowHover: {
    backgroundColor: colors.gray[100],
  },
  tableCell: {
    fontSize: '14px',
    color: colors.ink.black,
  },
  buyerLink: {
    color: colors.brand.blue,
    textDecoration: 'none',
    fontWeight: 500,
    cursor: 'pointer',
  },
  orderItemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  orderItemImage: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    objectFit: 'cover',
  },
  orderItemImagePlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: colors.gray[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  orderItemName: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    color: colors.white,
    fontSize: '12px',
    fontWeight: 600,
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    padding: 0,
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    border: '1px solid #FECACA',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  cancelButtonLoading: {
    fontSize: '12px',
    fontWeight: 600,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: `1px solid ${colors.gray[200]}`,
  },
  pageButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    backgroundColor: colors.white,
    color: colors.ink.black,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  pageInfo: {
    fontSize: '14px',
    color: colors.gray[600],
  },
};
